import React, { useCallback } from "react";
import { ZoomKeyframe } from "../../../../types";
import { getScaleColorClass } from "../../../../utils/zoom/zoom-timeline-utils";

interface ZoomKeyframeBlockProps {
  keyframe: ZoomKeyframe;
  leftPercent: number;
  widthPercent: number;
  isSelected: boolean;
  isDragging: boolean;
  onSelect: () => void;
  onDragStart: (
    mode: "move" | "resize-left" | "resize-right",
    e: React.MouseEvent
  ) => void;
}

/**
 * Individual zoom keyframe block rendered within the timeline zoom overlay.
 * Displays as a colored block with drag handles for moving and resizing.
 */
export const ZoomKeyframeBlock: React.FC<ZoomKeyframeBlockProps> = ({
  keyframe,
  leftPercent,
  widthPercent,
  isSelected,
  isDragging,
  onSelect,
  onDragStart,
}) => {
  const colorClass = getScaleColorClass(keyframe.scale);

  // Determine if block is narrow (for hiding label)
  const isNarrow = widthPercent < 8; // Roughly 32px at typical widths

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onSelect();
      onDragStart("move", e);
    },
    [onSelect, onDragStart]
  );

  const handleResizeLeftMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onSelect();
      onDragStart("resize-left", e);
    },
    [onSelect, onDragStart]
  );

  const handleResizeRightMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onSelect();
      onDragStart("resize-right", e);
    },
    [onSelect, onDragStart]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onSelect();
    },
    [onSelect]
  );

  return (
    <div
      className={`zoom-keyframe-block absolute top-0.5 bottom-0.5 ${colorClass} rounded-sm transition-all cursor-pointer ${
        isSelected ? "ring-2 ring-primary ring-offset-1 z-20" : "z-10"
      } ${isDragging ? "opacity-70 shadow-lg" : "opacity-80 hover:opacity-100"}`}
      style={{
        left: `${leftPercent}%`,
        width: `${Math.max(widthPercent, 3)}%`,
        minWidth: "24px",
      }}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
    >
      {/* Left resize handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 transition-colors z-30 rounded-l-sm"
        onMouseDown={handleResizeLeftMouseDown}
      />

      {/* Center content area */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {!isNarrow && (
          <span className="text-[9px] font-semibold text-white truncate px-1 select-none drop-shadow-sm">
            {keyframe.scale.toFixed(1)}x
          </span>
        )}
      </div>

      {/* Right resize handle */}
      <div
        className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 transition-colors z-30 rounded-r-sm"
        onMouseDown={handleResizeRightMouseDown}
      />
    </div>
  );
};
